
export interface EmployeeRequest {
	customer: string
	accessLevel: EmployeeAccessLevel
	state: EmployeeStates
	name: string
	title?: string
	department?: string
	address?: string
	emailAddress?: string
	password?: string
	noEmail: boolean
	smsValidation: boolean
	smsNumber?: string
	authenticatorValidation: boolean
	language: 'en' | 'da'
	ipRestriction?: string
	primaryContact: boolean
	notifyOnGeneralUsageLimit: boolean
	notifyOnGeneralUsageLimitReseller: boolean
	notifyOnAccountErrorByEmail: boolean
	notifyOnAccountErrorBySms: boolean
	notifyOnNewMvnoAccountReseller: boolean
	notifyOnNewSipAccountReseller: boolean
	notifyOnNewPortingReseller: boolean
	notes?:	string
	subscriptions:	string[]
	shortcuts: EmployeeShortcut[]
	persistentTokens: string[]
	custom?: unknown
	birthdate?: Date
	picture?: string
	pinCode?: string
	presence: Omit<EmployeePresence, 'overrides'>
	autoPresence?: EmployeeAutoPresence[]
	communicatorAutoState?: EmployeeCommunicatorAutoState[]
	voicemail?: {
		notification: {
			email: boolean
			sms: boolean
		},
		requiresPinCode: boolean
		greetingOnly: boolean
		defaultGreeting?: string
		offWorkGreeting?: string
		holidayGreeting?: string
	}
	recording?: {
		mode: EmployeeRecordingMode
		quality: EmployeeRecordingQuality
		retension: EmployeeRecordingRetension
		notification: {
			email: boolean
			attach: boolean
			maxSize: number
		}
	}
	features: EmployeeFeature[]
	viewOtherPartyOverride?: EmployeeViewOtherPartyOverride
	billingCustomer?: string
	product?: string
	newProduct?: string
	invoicedUntil?: Date
	startDate?: Date
	deleteDate?: Date
	addonProducts: EmployeeAddOnProduct[]
	ms365?: {
		id?: string
		calendarSetsPresence: boolean
		teamsSetsPresence: boolean
		presenceSync: boolean
		doNotSyncContacts: boolean
		doNotSyncCalendar: boolean
	}
}

export interface EmployeeResponse {
	_id: string
	customer: string
	accessLevel: EmployeeAccessLevel
	state: EmployeeStates
	name: string
	title?: string
	department?: string
	address?: string
	emailAddress?: string
	password?: string
	noEmail: boolean
	smsValidation: boolean
	smsNumber?: string
	authenticatorValidation: boolean
	language: 'en' | 'da'
	ipRestriction?: string
	primaryContact: boolean
	notifyOnGeneralUsageLimit: boolean
	notifyOnGeneralUsageLimitReseller: boolean
	notifyOnAccountErrorByEmail: boolean
	notifyOnAccountErrorBySms: boolean
	notifyOnNewMvnoAccountReseller: boolean
	notifyOnNewSipAccountReseller: boolean
	notifyOnNewPortingReseller: boolean
	notes?:	string
	subscriptions:	string[]
	shortcuts: EmployeeShortcut[]
	persistentTokens: string[]
	custom?: unknown
	birthdate?: Date
	picture?: string
	pinCode?: string
	presence: EmployeePresence
	autoPresence?: EmployeeAutoPresence[]
	communicatorAutoState?: EmployeeCommunicatorAutoState[]
	voicemail?: {
		notification: {
			email: boolean
			sms: boolean
		},
		requiresPinCode: boolean
		greetingOnly: boolean
		defaultGreeting?: string
		offWorkGreeting?: string
		holidayGreeting?: string
	}
	recording?: {
		mode: EmployeeRecordingMode
		quality: EmployeeRecordingQuality
		retension: EmployeeRecordingRetension
		notification: {
			email: boolean
			attach: boolean
			maxSize: number
		}
	}
	features: EmployeeFeature[]
	viewOtherPartyOverride?: EmployeeViewOtherPartyOverride
	billingCustomer?: string
	product?: string
	newProduct?: string
	invoicedUntil?: Date
	startDate?: Date
	deleteDate?: Date
	addonProducts: EmployeeAddOnProduct[]
	ms365?: {
		id?: string
		calendarSetsPresence: boolean
		teamsSetsPresence: boolean
		presenceSync: boolean
		doNotSyncContacts: boolean
		doNotSyncCalendar: boolean
	}
}

export interface EmployeeShortcut {
	index: number
	url: string
	label: string
}

export interface EmployeePresence {
	state: EmployeePresenceState
	location?: string
	message?: string
	expire?: Date | null
	overrides: EmployeePresenceOverride[]
}

export interface EmployeePresenceOverride {
	id: string
	source: EmployeePresenceOverrideSource
	state: EmployeePresenceState
}

export interface EmployeeAutoPresence {
	weekDays: number[]
	timeOfDay: string
	state: Extract<EmployeePresenceState, 'AVAILABLE' | 'BUSY' | 'OUT_OF_OFFICE' | 'OFF_WORK'>
}

export interface EmployeeCommunicatorAutoState {
	mode: 'CONNECT' | 'DISCONNECT'
	ip?: string
	presenceState?: EmployeePresenceState
	presenceLocation?: string
	presenceMessage?: string
	queues: { _id: string, mode: 'JOIN' | 'LEAVE', priority?: number }[]
	variables: { name: string, value?: string }[]
	switches: { name: string, value: boolean }[]
}

export interface EmployeeAddOnProduct {
	_id: string
	product: string
	name?: string
	description?: string
	invoicedUntil?: Date
	unitPrice?: number
	units: number
	start?: Date
	terminate?: Date
}

/** Employee access levels */
export enum EmployeeAccessLevel {
	/** For employees who only have hosted PBX phone(s), but cannot login. */
	NO_LOGIN = 'NO_LOGIN',
	/** Access to own basic settings: voicemail, call forwarding, call detail records. */
	PERSONAL = 'PERSONAL',
	/** View the configuration of the customer, but not change anything. */
	VIEWER = 'VIEWER',
	/** View and configure the customer, but not perform actions that incur additional cost (e.g. purchase a subscription). */
	MANAGER = 'MANAGER',
	/** View, configure and purchase - full access to the customer. */
	OWNER = 'OWNER',
	/** Only available if customer is a reseller. Gives full access to all the customers that belongs to the reseller, or sub resellers. */
	RESELLER = 'RESELLER',
	/** Only available for admin user that handles resellers. Gives full access to all resellers and their customers. */
	RESELLER_ADMIN = 'RESELLER_ADMIN',
	/** Telecom X administrative employee (only functional at Telecom X office). */
	ADMIN = 'ADMIN'
}

/** Employee access levels as numbers for comparison */
export enum EmployeeAccessLevelNumber {
	/** For employees who only have hosted PBX phone(s), but cannot login. */
	NO_LOGIN = 0,
	/** Access to own basic settings: voicemail, call forwarding, call detail records. */
	PERSONAL = 10,
	/** View the configuration of the customer, but not change anything. */
	VIEWER = 20,
	/** View and configure the customer, but not perform actions that incur additional cost (e.g. purchase a subscription). */
	MANAGER = 30,
	/** View, configure and purchase - full access to the customer. */
	OWNER = 40,
	/** Only available if customer is a reseller. Gives full access to all the customers that belongs to the reseller, or sub resellers. */
	RESELLER = 100,
	/** Only available for admin user that handles resellers. Gives full access to all resellers and their customers. */
	RESELLER_ADMIN = 200,
	/** Telecom X administrative employee (only functional at Telecom X office). */
	ADMIN = 255
}

/** Employee feature flags */
export enum EmployeeFeature {
	/** Access to PlayMaker support site. */
	PLAYMAKERSTATUS = 'PLAYMAKERSTATUS',
	/** permission to edit own PBX dashboards. */
	DASHBOARD_EDITOR = 'DASHBOARD_EDITOR',
	/** can be assigned tasks (only ADMIN). */
	HUMAN_TASK = 'HUMAN_TASK',
	/** access core porting (only RESELLER_ADMIN). */
	PORTING = 'PORTING',
	/** access core tele (only RESELLER_ADMIN). */
	TELE = 'TELE',
	/** access core finance (only RESELLER_ADMIN ). */
	FINANCE = 'FINANCE',
	/** access core TV and flexcare (only RESELLER_ADMIN). */
	TV = 'TV',
	/** Acces to Communicator debugging */
	COMMUNICATOR_DEBUG = 'COMMUNICATOR_DEBUG',
	/** Access to core network management (only RESELLER_ADMIN) */
	NETWORK = 'NETWORK',
	/** Access to perform FlexCare Swap */
	FLEXCARESWAP = 'FLEXCARESWAP'
}

/** Employee reocrding modes */
export enum EmployeeRecordingMode {
	/** All calls are recorded */
	ALWAYS = 'ALWAYS',
	/** Only record when requested */
	DEMAND = 'DEMAND',
	/** Recording is disabled */
	NEVER = 'NEVER'
}

/** Employee recording qualities */
export enum EmployeeRecordingQuality {
	/** 8KHz MP3 mono */
	NORMAL = 'NORMAL',
	/** Reduced audio quality MP3 */
	COMPACT = 'COMPACT',
	/** 16Khz MP# stereo (each party in seperate channel) */
	HIGH = 'HIGH'
}

/** Employee override for weather other may see who the employee is talking to */
export enum EmployeeViewOtherPartyOverride {
	/** No override enabled */
	NONE = 'NONE',
	/** Always hide */
	HIDE = 'HIDE',
	/** Hide when other party is in personal phonebook */
	HIDE_PERSONAL_PHONEBOOK = 'HIDE_PERSONAL_PHONEBOOK',
	/** Hide when employee presence state is OFF_WORK or HOLIDAY */
	HIDE_OFF_WORK = 'HIDE_OFF_WORK',
	/** Hide when otehr party is in personal phonebook or employee presence state is OFF_WORK or HOLIDAY */
	HIDE_PERSONAL_PHONEBOOK_AND_OFF_WORK = 'HIDE_PERSONAL_PHONEBOOK_AND_OFF_WORK'
}

/** Employee presence states */
export enum EmployeePresenceState {
	/** Available to accept calls */
	AVAILABLE = 'AVAILABLE',
	/** Busy - do not disturb */
	BUSY = 'BUSY',
	/** When not in the office */
	OUT_OF_OFFICE = 'OUT_OF_OFFICE',
	/** Not working */
	OFF_WORK = 'OFF_WORK',
	/** On holiday */
	HOLIDAY = 'HOLIDAY'
}

/** Employee presence states that can be applied to Auto Presence */
export type EmployeeAutoPresenceState = Extract<EmployeePresenceState, 'AVAILABLE' | 'BUSY' | 'OUT_OF_OFFICE' | 'OFF_WORK'>

/** Employee states */
export enum EmployeeStates {
	/** Employee is in normal operation */
	ENABLED = 'ENABLED',
	/** Employee has been disabled, cannot login or use any features assigned to the employee */
	DISABLED = 'DISABLED',
	/** Employee is deleted */
	DELETED = 'DELETED'
}

export enum EmployeePresenceOverrideSource {
	/** Calendar event */
	MS365_CALENDAR = 'MS365_CALENDAR',
	/** Teams meeting */
	MS365_TEAMS = 'MS365_TEAMS'
}

export type EmployeeRecordingRetension = 1 | 3 | 7 | 14 | 30 | 180 | 730

export type EmployeeUploadPictureRequest = FormData

export interface EmployeeUploadPictureResponse {
	/** True on success */
	succes: boolean
	/** Id of the picture on the image server */
	picture: string
}
